import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Services from "@/pages/Services";
import Parts from "@/pages/Parts";
import QuoteCalculator from "@/pages/QuoteCalculator";
import Quotes from "@/pages/Quotes";
import QuoteDetail from "@/pages/QuoteDetail";
import WorkOrders from "@/pages/WorkOrders";
import Maintenance from "@/pages/Maintenance";
import Tools from "@/pages/Tools";
import Reports from "@/pages/Reports";
import Market from "@/pages/Market";
import Materials from "@/pages/Materials";
import Assist from "@/pages/Assist";
import Network from "@/pages/Network";
import Mentor from "@/pages/Mentor";
import Partner from "@/pages/Partner";
import Custos from "@/pages/Custos";
import Equipe from "@/pages/Equipe";
import Contratos from "@/pages/Contratos";
import Directory from "@/pages/Directory";
import DirectoryProfile from "@/pages/DirectoryProfile";
import Plans from "@/pages/Plans";
import PaymentResult from "@/pages/PaymentResult";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Legal from "@/pages/Legal";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Protected({ children }) {
  const { data, company } = useAuth();
  const location = useLocation();
  if (data === undefined) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-sky-600 animate-spin" data-testid="auth-loading" />
      </div>
    );
  }
  if (data === null) return <Navigate to="/login" replace />;
  if (!company?.onboarding_done && location.pathname !== "/app/onboarding") {
    return <Navigate to="/app/onboarding" replace />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Toaster richColors position="top-center" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/entrar" element={<Navigate to="/login" replace />} />
            <Route path="/cadastro" element={<Register />} />
            <Route path="/recuperar-senha" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/termos" element={<Legal />} />
            <Route path="/privacidade" element={<Legal />} />
            <Route path="/diretorio" element={<Directory />} />
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="dashboard" element={<Navigate to="/app" replace />} />
              <Route path="clientes" element={<Customers />} />
              <Route path="clientes/:id" element={<CustomerDetail />} />
              <Route path="servicos" element={<Services />} />
              <Route path="pecas" element={<Parts />} />
              <Route path="calculadora" element={<QuoteCalculator />} />
              <Route path="orcamentos" element={<Quotes />} />
              <Route path="orcamentos/:id" element={<QuoteDetail />} />
              <Route path="os" element={<WorkOrders />} />
              <Route path="manutencao" element={<Maintenance />} />
              <Route path="ferramentas" element={<Tools />} />
              <Route path="relatorios" element={<Reports />} />
              <Route path="mercado" element={<Market />} />
              <Route path="materiais" element={<Materials />} />
              <Route path="assist" element={<Assist />} />
              <Route path="network" element={<Network />} />
              <Route path="mentor" element={<Mentor />} />
              <Route path="partner" element={<Partner />} />
              <Route path="custos" element={<Custos />} />
              <Route path="equipe" element={<Equipe />} />
              <Route path="contratos" element={<Contratos />} />
              <Route path="diretorio" element={<DirectoryProfile />} />
              <Route path="planos" element={<Plans />} />
              <Route path="pagamento/sucesso" element={<PaymentResult type="success" />} />
              <Route path="pagamento/cancelado" element={<PaymentResult type="cancel" />} />
              <Route path="admin" element={<Admin />} />
              <Route path="configuracoes" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
